import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { PVZSelector } from '../components/checkout/PVZSelector'
import TBankPayment from '../components/checkout/TBankPayment'
import { useCartStore } from '../store/cartStore'
import { useUIStore } from '../store/uiStore'
import { ordersService } from '../services/orders'
import { paymentsService } from '../services/payments'

const CheckoutPage = () => {
  const navigate = useNavigate()
  const items = useCartStore((state) => state.items)
  const getTotalPrice = useCartStore((state) => state.getTotalPrice)
  const clearCart = useCartStore((state) => state.clearCart)
  const setCustomBack = useUIStore((state) => state.setCustomBack)
  
  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = 3

  const [customerInfo, setCustomerInfo] = useState({
    fullName: '',
    phone: '',
    email: '',
  })
  // Автоматически определяем страну по первой цифре
  const [phoneCountry, setPhoneCountry] = useState(null) // null - не определена, 'ru' - Россия, 'by' - Беларусь
  const [selectedPVZ, setSelectedPVZ] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState('redirect') // 'redirect' - редирект на форму, 'qr' - QR-код
  const [legalConfirmed, setLegalConfirmed] = useState(false)

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  
  const [order, setOrder] = useState(null)
  const [paymentData, setPaymentData] = useState(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const pollingRef = useRef(null)

  const API_URL = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || ''

  const formatPrice = (value) => {
    return new Intl.NumberFormat('ru-RU').format(value)
  }

  // Polling для проверки статуса платежа (fallback на случай если iframe не отправил сообщение)
  useEffect(() => {
    if (showPaymentForm && order?.id) {
      pollingRef.current = setInterval(async () => {
        try {
          const response = await paymentsService.checkStatusByOrder(order.id)
          // Останавливаем polling при любом финальном статусе
          if (response.status === 'succeeded' || response.status === 'failed' || response.status === 'cancelled') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current)
              pollingRef.current = null
            }
            // Переходим на страницу заказа только при успехе
          if (response.status === 'succeeded') {
            clearCart()
            navigate(`/orders/${order.id}`)
            }
          }
        } catch (err) {
          console.log('Payment status check:', err.message)
        }
      }, 3000)

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      }
    }
  }, [showPaymentForm, order?.id, navigate, clearCart])

  useEffect(() => {
    // Scroll to active element when window resizes (keyboard opens)
    const handleResize = () => {
      if (document.activeElement?.tagName === 'INPUT') {
        setTimeout(() => {
          document.activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 100)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const validateStep1 = () => {
    const newErrors = {}
    if (!customerInfo.fullName.trim()) {
      newErrors.fullName = 'Введите ФИО'
    }
    // Remove non-digits for validation
    const rawPhone = customerInfo.phone.replace(/\D/g, '')
    
    // Определяем страну по номеру, если еще не определена
    let detectedCountry = phoneCountry
    if (!detectedCountry && rawPhone.length > 0) {
      if (rawPhone.startsWith('375')) {
        detectedCountry = 'by'
      } else if (rawPhone.startsWith('7') || rawPhone.startsWith('8')) {
        detectedCountry = 'ru'
      }
    }
    
    // Валидация в зависимости от определенной страны
    if (detectedCountry === 'ru') {
      // Российский номер: +7 + 10 цифр = 11 цифр всего
      // Убираем ведущую 8, если есть
      const normalizedPhone = rawPhone.startsWith('8') ? '7' + rawPhone.substring(1) : rawPhone
      if (!normalizedPhone || normalizedPhone.length < 11 || !normalizedPhone.startsWith('7')) {
        newErrors.phone = 'Введите корректный номер'
      }
    } else if (detectedCountry === 'by') {
      // Белорусский номер: +375 + 9 цифр (2 код оператора + 7 номер) = 12 цифр всего
      if (!rawPhone || rawPhone.length < 12 || !rawPhone.startsWith('375')) {
      newErrors.phone = 'Введите корректный номер'
      } else {
        // Проверяем код оператора (29, 33, 44, 25)
        const operatorCode = rawPhone.substring(3, 5)
        const validOperators = ['29', '33', '44', '25']
        if (!validOperators.includes(operatorCode)) {
          newErrors.phone = 'Введите корректный номер оператора (29, 33, 44, 25)'
        }
      }
    } else if (rawPhone.length > 0) {
      // Если номер введен, но страна не определена
      newErrors.phone = 'Введите корректный номер (начните с 7, 8 или 375)'
    } else {
      newErrors.phone = 'Введите номер телефона'
    }
    
    if (!customerInfo.email.trim()) {
      newErrors.email = 'Введите email'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      newErrors.email = 'Неверный формат'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep2 = () => {
    if (!selectedPVZ) {
      setErrors({ pvz: 'Выберите пункт выдачи' })
      return false
    }
    setErrors({})
    return true
  }

  const handleNext = () => {
    let isValid = false
    if (currentStep === 1) isValid = validateStep1()
    else if (currentStep === 2) isValid = validateStep2()
    else isValid = true

    if (isValid && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      setErrors({})
    }
  }

  // Handle header back button
  useEffect(() => {
    const handleHeaderBack = () => {
      if (showPaymentForm) {
        setShowPaymentForm(false)
      } else if (currentStep > 1) {
        setCurrentStep(prev => prev - 1)
        setErrors({})
      } else {
        navigate(-1)
      }
    }

    setCustomBack(handleHeaderBack)
    return () => setCustomBack(null)
  }, [currentStep, showPaymentForm, navigate, setCustomBack])

  const handlePhoneChange = (e) => {
    let value = e.target.value
    // Получаем только цифры
    let digits = value.replace(/\D/g, '')

    // Проверяем, не удалил ли пользователь все символы (включая +)
    // Если поле пустое, сбрасываем страну
    if (value.trim() === '' || digits.length === 0) {
      setPhoneCountry(null)
      setCustomerInfo(prev => ({ ...prev, phone: '' }))
      if (errors.phone) setErrors(prev => ({ ...prev, phone: null }))
      return
    }
    
    // Определяем страну автоматически по первой цифре
    let detectedCountry = phoneCountry
    if (digits.length > 0) {
      if (digits.startsWith('375')) {
        detectedCountry = 'by'
      } else if (digits[0] === '7' || digits[0] === '8') {
        detectedCountry = 'ru'
      } else if (digits[0] === '3' && digits.length < 3) {
        // Пользователь начал вводить 3, но еще не ввел 375
        // Пока не определяем страну, показываем как есть
        detectedCountry = null
      }
    }
    
    // Обновляем состояние страны, если она изменилась
    if (detectedCountry !== phoneCountry) {
      setPhoneCountry(detectedCountry)
    }

    if (detectedCountry === 'ru') {
      // Российский номер
      // Если первая цифра 7 или 8, оставляем только 7 и остальные цифры
      if (digits.length > 0) {
        if (digits[0] === '8') {
          digits = '7' + digits.substring(1)
        } else if (digits[0] !== '7') {
          // Если не начинается с 7 или 8, добавляем 7
          digits = '7' + digits
        }
      }

      // Ограничиваем длину 11 цифрами (7 + 10)
      digits = digits.substring(0, 11)

    // Форматируем: +7 (XXX) XXX-XX-XX
    let formattedValue = '+7'
    
      if (digits.length > 1) {
        const localDigits = digits.substring(1) // Убираем ведущую 7
        formattedValue += ' (' + localDigits.substring(0, 3)
        if (localDigits.length > 3) {
          formattedValue += ') ' + localDigits.substring(3, 6)
    }
        if (localDigits.length > 6) {
          formattedValue += '-' + localDigits.substring(6, 8)
    }
        if (localDigits.length > 8) {
          formattedValue += '-' + localDigits.substring(8, 10)
        }
      }

      setCustomerInfo(prev => ({ ...prev, phone: formattedValue }))
    } else if (detectedCountry === 'by') {
      // Белорусский номер
      // Если начинается с 375, убираем код страны для форматирования
      let localDigits = digits
      if (digits.startsWith('375')) {
        localDigits = digits.substring(3)
      }

      // Ограничиваем длину 9 цифрами (2 код оператора + 7 номер)
      localDigits = localDigits.substring(0, 9)

      // Форматируем: +375 (XX) XXX-XX-XX
      let formattedValue = '+375'
      
      if (localDigits.length > 0) {
        formattedValue += ' (' + localDigits.substring(0, 2) // Код оператора
        if (localDigits.length > 2) {
          formattedValue += ') ' + localDigits.substring(2, 5) // Первые 3 цифры номера
        }
        if (localDigits.length > 5) {
          formattedValue += '-' + localDigits.substring(5, 7) // Следующие 2 цифры
        }
        if (localDigits.length > 7) {
          formattedValue += '-' + localDigits.substring(7, 9) // Последние 2 цифры
        }
    }

    setCustomerInfo(prev => ({ ...prev, phone: formattedValue }))
    } else {
      // Страна еще не определена, показываем цифры без форматирования
      setCustomerInfo(prev => ({ ...prev, phone: digits }))
    }

    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: null }))
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (name === 'phone') {
      handlePhoneChange(e)
      return
    }
    setCustomerInfo({ ...customerInfo, [name]: value })
    if (errors[name]) {
      setErrors({ ...errors, [name]: null })
    }
  }

  const handlePVZSelect = (pvz) => {
    setSelectedPVZ(pvz)
    if (errors.pvz) {
      setErrors({ ...errors, pvz: null })
    }
  }

  const handleFinalSubmit = async () => {
    if (!legalConfirmed) {
      setErrors({ submit: 'Пожалуйста, подтвердите ознакомление с правилами' })
      return
    }

    setLoading(true)
    try {
      const orderData = {
        cart: items.map(item => ({
          product_id: item.productId,
          variant_id: item.variantId,
          quantity: item.quantity,
        })),
        delivery_address_data: {
          pvz_code: selectedPVZ.code,
          full_name: customerInfo.fullName,
          phone: customerInfo.phone,
          email: customerInfo.email,
        },
        pvz_code: selectedPVZ.code,
        pvz_name: selectedPVZ.name || '',
        pvz_address: selectedPVZ.address || '',
        pvz_latitude: selectedPVZ.latitude || null,
        pvz_longitude: selectedPVZ.longitude || null,
      }

      const createdOrder = await ordersService.create(orderData)
      setOrder(createdOrder)
      
      const returnUrl = `${window.location.origin}/orders/${createdOrder.id}`
      // Передаем 'sbp' для редиректа, 'sbp_qr' для QR-кода
      const backendPaymentMethod = paymentMethod === 'qr' ? 'sbp_qr' : 'sbp'
      const paymentInit = await paymentsService.initPayment(
        createdOrder.id,
        getTotalPrice(),
        returnUrl,
        customerInfo.email,
        backendPaymentMethod
      )

      // Для QR-кода проверяем наличие qr_data, для редиректа - payment_url
      if (paymentMethod === 'qr' && paymentInit.qr_data) {
        setPaymentData(paymentInit)
        setShowPaymentForm(true)
      } else if (paymentMethod === 'redirect' && paymentInit.payment_url) {
        setPaymentData(paymentInit)
        setShowPaymentForm(true)
      } else {
        navigate(`/orders/${createdOrder.id}`)
      }
    } catch (error) {
      // Показываем дружелюбное сообщение, не спамим консоль stack'ом
      console.warn('Ошибка оформления заказа:', error?.message || error)
      
      // Если ответ содержит HTML (например, при ошибке 500), пытаемся извлечь JSON
      let errorData = null
      try {
        if (error.response?.data) {
          // Если это строка, пытаемся распарсить как JSON
          if (typeof error.response.data === 'string') {
            // Если это HTML, не парсим
            if (error.response.data.trim().startsWith('<!')) {
              errorData = null
            } else {
              try {
                errorData = JSON.parse(error.response.data)
              } catch {
                errorData = { error: error.response.data }
              }
            }
          } else {
            errorData = error.response.data
          }
        }
      } catch (e) {
        // Игнорируем ошибки парсинга
      }
      
      // Извлекаем сообщение об ошибке
      let errorMessage = ''
      
      if (errorData?.error && /SSL|CERTIFICATE|traceback|Exception|T-Bank API|Ошибка соединения/i.test(String(errorData.error))) {
        errorMessage = 'Не удалось создать платёж. Попробуйте ещё раз.'
      } else if (errorData?.error) {
        // Используем сообщение из ответа сервера
        errorMessage = errorData.error
      } else if (errorData?.message) {
        errorMessage = errorData.message
      } else if (error.response?.status === 400) {
        // Для 400 статуса показываем сообщение о товарах
        errorMessage = 'Товар закончился и больше недоступен для покупки. Обновите корзину и попробуйте снова.'
      } else if (error.response?.status === 500) {
        errorMessage = 'Не удалось создать платёж. Попробуйте ещё раз.'
      } else {
        errorMessage = 'Не удалось создать платёж. Попробуйте ещё раз.'
      }
      
      setErrors({ submit: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  const handlePaymentSuccess = () => {
    // Останавливаем polling при успешной оплате
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    clearCart()
    navigate(`/orders/${order.id}`)
  }

  const handlePaymentError = () => {
    // Останавливаем polling при ошибке
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    setShowPaymentForm(false)
    setErrors({ submit: 'Ошибка оплаты. Попробуйте снова.' })
  }

  const handlePaymentCancel = () => {
    // Останавливаем polling при отмене
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    setShowPaymentForm(false)
  }

  if (items.length === 0) {
    return (
      <div className="empty-state">
        <h2>Корзина пуста</h2>
        <button onClick={() => navigate('/')} className="add-to-cart-btn">
          В каталог
        </button>
      </div>
    )
  }

  return (
    <div className="checkout fade-in">
      {/* Step Indicator */}
      <div className="checkout-steps">
        {[1, 2, 3].map((step) => (
          <div 
            key={step} 
            className={`checkout-step ${step === currentStep ? 'active' : ''} ${step < currentStep ? 'completed' : ''}`}
          />
        ))}
      </div>

      {/* Step 1: Customer Info */}
      {currentStep === 1 && !showPaymentForm && (
        <div className="fade-in checkout-content-wrapper">
          <div className="checkout-title">Данные получателя</div>
          
          {/* Mini Order Summary */}
          <div className="order-summary-mini">
            {items.map((item, idx) => (
              <div key={idx} className="order-summary-item">
                <img
                  src={item.productImage?.startsWith('http') ? item.productImage : `${API_URL}${item.productImage}`}
                  alt={item.productName}
                  className="order-summary-thumb"
                />
                <div className="order-summary-details">
                  <div className="order-summary-name">{item.productName}</div>
                  <div className="order-summary-meta">
                    {item.variantSize && `${item.variantSize}`}
                    {item.variantSize && item.variantColor && ' / '}
                    {item.variantColor} × {item.quantity}
                  </div>
                </div>
                <div className="order-summary-price">{formatPrice(item.price * item.quantity)} ₽</div>
              </div>
            ))}
            <div className="order-summary-total">
              <span className="order-summary-total-label">Итого</span>
              <span className="order-summary-total-value">{formatPrice(getTotalPrice())} ₽</span>
            </div>
          </div>
          
          <div className="checkout-form">
            <input
              type="text"
              name="fullName"
              placeholder="ФИО получателя"
              value={customerInfo.fullName}
              onChange={handleInputChange}
              className={`checkout-input ${errors.fullName ? 'error' : ''}`}
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
            />
            {errors.fullName && <div className="checkout-error">{errors.fullName}</div>}
            
            <input
              type="tel"
              name="phone"
              placeholder="Телефон (начните с 7, 8 или 375)"
              value={customerInfo.phone}
              onChange={handlePhoneChange}
              className={`checkout-input ${errors.phone ? 'error' : ''}`}
              autoComplete="tel"
              autoCorrect="off"
              spellCheck="false"
            />
            {errors.phone && <div className="checkout-error">{errors.phone}</div>}
            
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={customerInfo.email}
              onChange={handleInputChange}
              className={`checkout-input ${errors.email ? 'error' : ''}`}
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
            />
            {errors.email && <div className="checkout-error">{errors.email}</div>}
          </div>
        </div>
      )}

      {/* Step 2: PVZ Selection */}
      {currentStep === 2 && !showPaymentForm && (
        <div className="fade-in checkout-content-wrapper">
          <div className="checkout-title">Пункт выдачи СДЭК</div>
          
          {selectedPVZ && (
            <div className="pvz-selected">
              <div className="pvz-selected-info">
                <div className="pvz-selected-code">{selectedPVZ.name}</div>
                <div className="pvz-selected-address">{selectedPVZ.address}</div>
              </div>
              <button 
                className="pvz-change-btn"
                onClick={() => setSelectedPVZ(null)}
              >
                Изменить
              </button>
            </div>
          )}
          
          {!selectedPVZ && (
            <PVZSelector 
              onSelect={handlePVZSelect} 
              cityFrom="Москва"
              defaultCity={null}
            />
          )}
          
          {errors.pvz && <div className="checkout-error" style={{ marginTop: '16px' }}>{errors.pvz}</div>}
        </div>
      )}

      {/* Step 3: Payment */}
      {currentStep === 3 && (
        <div className="fade-in checkout-content-wrapper">
          {showPaymentForm && paymentData ? (
            <>
              <div className="checkout-title">Оплата заказа {order?.order_number}</div>
              
              <TBankPayment
                paymentId={paymentData.tbank_payment_id}
                paymentUrl={paymentData.payment_url}
                orderId={order?.id}
                amount={getTotalPrice()}
                qrData={paymentData.qr_data}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                onCancel={handlePaymentCancel}
              />
              
              <button
                onClick={handlePaymentCancel}
                className="checkout-btn checkout-btn-back"
                style={{ width: '100%', marginTop: '16px' }}
              >
                ← Назад
              </button>
            </>
          ) : (
            <>
              {/* Order Summary */}
              <div className="order-summary-mini">
                {items.map((item, idx) => (
                  <div key={idx} className="order-summary-item">
                    <img
                      src={item.productImage?.startsWith('http') ? item.productImage : `${API_URL}${item.productImage}`}
                      alt={item.productName}
                      className="order-summary-thumb"
                    />
                    <div className="order-summary-details">
                      <div className="order-summary-name">{item.productName}</div>
                      <div className="order-summary-meta">
                        {item.variantSize && `${item.variantSize}`} × {item.quantity}
                      </div>
                    </div>
                    <div className="order-summary-price">{formatPrice(item.price * item.quantity)} ₽</div>
                  </div>
                ))}
                
                {selectedPVZ && (
                  <div style={{ 
                    paddingTop: '12px', 
                    borderTop: '1px solid #eee',
                    fontSize: '11px',
                    color: '#666',
                    marginTop: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <div>📍 {selectedPVZ.address}</div>
                    <div>👤 {customerInfo.fullName}, {customerInfo.phone}</div>
                  </div>
                )}
                
                <div className="order-summary-total">
                  <span className="order-summary-total-label">К оплате</span>
                  <span className="order-summary-total-value">{formatPrice(getTotalPrice())} ₽</span>
                </div>
                <div style={{ fontSize: '10px', color: '#666', marginTop: '6px', lineHeight: '1.4' }}>
                  Доставка через CDEK и оплачивается отдельно при получении товара.
                </div>
              </div>

              <div className="legal-confirmation" style={{ marginTop: '24px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div 
                  onClick={() => setLegalConfirmed(!legalConfirmed)}
                  style={{
                    width: '20px',
                    height: '20px',
                    border: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    background: legalConfirmed ? '#000' : '#fff',
                    borderColor: legalConfirmed ? '#000' : '#ddd',
                    transition: 'all 0.2s'
                  }}
                >
                  {legalConfirmed && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2">
                      <polyline points="1 6 4 9 11 2" />
                    </svg>
                  )}
                </div>
                <div style={{ fontSize: '11px', lineHeight: '1.4', color: '#666', paddingTop: '2px' }}>
                  Я ознакомлен(а) с <Link to="/about" target="_blank" style={{ color: '#000', textDecoration: 'underline' }}>информацией о продавце</Link>, <Link to="/terms" target="_blank" style={{ color: '#000', textDecoration: 'underline' }}>условиями</Link> и <Link to="/privacy" target="_blank" style={{ color: '#000', textDecoration: 'underline' }}>политикой конфиденциальности</Link>.
                </div>
              </div>

              {errors.submit && (
                <div className="checkout-error" style={{ marginTop: '16px', textAlign: 'center' }}>
                  {errors.submit}
                </div>
              )}

              {/* Выбор метода оплаты */}
              <div style={{ marginTop: '24px' }}>
                <div style={{
                  fontSize: '10px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#666',
                  marginBottom: '12px'
                }}>
                  Способ оплаты
                </div>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  flexDirection: 'column'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    border: `2px solid ${paymentMethod === 'redirect' ? '#000' : '#ddd'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: paymentMethod === 'redirect' ? '#f9f9f9' : '#fff'
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="redirect"
                      checked={paymentMethod === 'redirect'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', marginBottom: '4px' }}>СБП</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>Система Быстрых Платежей, выбор банка при оплате</div>
                    </div>
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    border: `2px solid ${paymentMethod === 'qr' ? '#000' : '#ddd'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: paymentMethod === 'qr' ? '#f9f9f9' : '#fff'
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="qr"
                      checked={paymentMethod === 'qr'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', marginBottom: '4px' }}>QR</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>Отсканируйте QR-код в приложении банка</div>
                    </div>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Navigation */}
      {!showPaymentForm && (
        <div className="checkout-nav">
          {currentStep < totalSteps ? (
            <button
              onClick={handleNext}
              disabled={loading}
              className="checkout-btn checkout-btn-next"
            >
              Далее
            </button>
          ) : (
            <div style={{ position: 'relative', width: '100%' }}>
            <button
              onClick={handleFinalSubmit}
                disabled={loading || !legalConfirmed}
              className="checkout-btn checkout-btn-next"
                style={{ width: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <span>{loading ? 'Обработка...' : 'Оплатить'}</span>
                <span style={{
                  position: 'absolute',
                  right: '16px',
                  fontSize: '12px',
                  color: '#888',
                  pointerEvents: 'none'
                }}>
                  {paymentMethod === 'qr' ? 'QR' : 'СБП'}
                </span>
            </button>
            </div>
          )}
        </div>
      )}
    </div>
  )

}

export default CheckoutPage
